import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { useAuth0 } from '#~/auth/index.js';
import type { Wiring } from '#~/common/editor/index.js';
import { logger } from '#~/logger/index.js';
import type { Api } from '@studio/design-system/dialog';
import { usePrompt } from '@studio/design-system/prompt';
import { ensureNotNil } from '@studio/js-utils';
import { generateId } from '@studio/models';
import { useLatestFn, useLazyRef, useMount, χref } from '@studio/react-utils/hooks';
import { pick } from 'lodash-es';
import type { RefObject } from 'react';
import { useContext } from 'react';
import { shallow } from 'zustand/shallow';
import { Context } from './context.js';
import { useProducer, useStore } from './store/hooks.js';
import { addDevice, Panel, removePreview } from './store/index.js';

/** @internal */
export const useWiring = (): Wiring => ensureNotNil(useContext(Context));

/** @internal */
export const useWorkflow = (apiRef: RefObject<Api | null>): Wiring => {
  const producer = useProducer();
  const prompt = usePrompt();

  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();

  const { roomId, ...store } = useStore(
    (µ) => pick(µ, ['agentId', 'candidate', 'panel', 'previews', 'roomId', 'settings']),
    shallow
  );

  const handleCleanup = useLatestFn(async () => {
    const token = await getAccessTokenSilently();

    const { previews } = store;

    // Remove all of the preview devices.
    if (previews.audio) {
      removePreview(previews.audio, token).catch((error) => {
        logger.info(error);
      });
    }

    // Reset the store.
    producer((draft) => {
      draft.source.selection = undefined;
      draft.previews.audio = undefined;
    });
  });

  const handleClose = useLatestFn(() => {
    handleCleanup();

    χref(apiRef)?.close(true);
  });

  const handleCancel = useLatestFn(() => {
    prompt.confirm({
      message:
        'Are you sure you want to exit now? By choosing to exit before saving you will lose all information about this audio source.',
      okay: {
        label: 'Yes, Exit and Cancel'
      },
      onConfirm: (answer: boolean) => {
        if (answer) {
          handleClose();
        }
      },
      title: 'Cancel add source'
    });
  });

  const handleCommit = useLatestFn(async () => {
    // Prevent multiple simultaneous saves
    if (store.settings?.isSaving) {
      return;
    }

    const { agentId, candidate } = store;
    const token = await getAccessTokenSilently();

    try {
      // Set saving state
      producer((draft) => {
        draft.settings.isSaving = true;
      });

      // Remove all of the preview devices before adding the "real" device.
      handleCleanup();

      track(EVENT_NAMES.ADD_AUDIO_SOURCE, EVENT_CATEGORIES.AUDIO_SOURCES, {
        deviceType: candidate.audio?.protocol?.toString() ?? '',
        model: candidate.modelId?.toString() ?? '',
        studioId: roomId.toString()
      });

      // Add the device!
      await addDevice(
        roomId,
        agentId,
        {
          ...candidate,
          agentId,
          id: generateId()
        },
        token
      );

      track(EVENT_NAMES.SUCCESSFULLY_ADD_AUDIO_SOURCE, EVENT_CATEGORIES.AUDIO_SOURCES);
    } catch (error) {
      logger.error(<Error>error);
    } finally {
      // Always reset saving state
      producer((draft) => {
        draft.settings.isSaving = false;
      });
    }

    χref(apiRef)?.close(true);
  });

  // Confirm cancellation, if necessary.
  const handleBeforeClose = useLatestFn((event: Event) => {
    if (store.panel > Panel.Source) {
      event.preventDefault();

      handleCancel();

      return;
    }

    handleClose();
  });

  // Cleanup when dialog closes.
  useMount(() =>
    χref(apiRef)?.on('visibility', (visible: boolean) => {
      if (!visible) {
        handleCleanup();
      }
    })
  );

  return useLazyRef(() => ({
    handleBeforeClose,
    handleCancel,
    handleClose,
    handleCommit
  })).current;
};
